import { useMemo } from "react";

type Props = {
  hexCodes: string[];
  image?: string | null;
  isActive: boolean;
  size?: string;
  outline?: "red" | "blue";
  hasHover?: boolean;
};

const ColorSwatch: React.FC<Props> = ({
  hexCodes,
  image,
  isActive,
  size = "17px",
  outline,
  hasHover = true,
}) => {
  const backgroundImage = useMemo(() => {
    if (hexCodes.length === 3) {
      return `linear-gradient(45deg, #${hexCodes[0]} 33%, #${hexCodes[1]} 33%, #${hexCodes[1]} 66%, #${hexCodes[2]} 66%)`;
    }

    if (hexCodes.length === 2) {
      return `linear-gradient(45deg, #${hexCodes[0]} 50%, #${hexCodes[1]} 50%)`;
    }

    if (image) {
      return `url(${image})`;
    }

    return "none";
  }, [image, hexCodes]);

  const backgroundColor = useMemo(() => {
    if (hexCodes.length === 1) {
      return `#${hexCodes[0]}`;
    }

    return "transparent";
  }, [hexCodes]);

  const className = useMemo(() => {
    const base = `w-full cursor-pointer bg-cover ${hasHover ? "hover:shadow-[0_0_0_3px]" : ""} after:content-[''] after:pb-[100%] after:block`;

    const shadowSize = (() => {
      if (isActive) {
        return "shadow-[0_0_0_3px]";
      }

      if (outline && !isActive) {
        return "shadow-[0_0_0_2px]";
      }

      return "shadow-[0_0_0_1px]";
    })();

    const shadowColor = (() => {
      if (!outline) {
        return "shadow-white200 hover:shadow-white200";
      }

      if (outline === "red") {
        return "shadow-red100 hover:shadow-red100";
      }

      if (outline === "blue") {
        return "shadow-blue hover:shadow-blue";
      }
    })();

    return `${base} ${shadowSize} ${shadowColor}`;
  }, [outline, isActive, hasHover]);

  return (
    <div
      role="presentation"
      style={{
        width: size,
        height: size,
        backgroundColor,
        backgroundImage,
      }}
      className={className}
    />
  );
};

export default ColorSwatch;
